import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useRecoilState } from 'recoil';
import { UserTypes } from '@/constants/SGFTUserTypes';
import api from '@/services/apiSgft';
import { newRegulatoryTrainingMatrixEditionModeAtom } from '@/state/NewRegulatoryTrainingMatrixEditionMode.atom';
import { newRegulatoryTrainingMatrixEditionsAtom } from '@/state/NewRegulatoryTrainingMatrixEditions.atom';
import { newRegulatoryTrainingMatrixFilterAtom } from '@/state/NewRegulatoryTrainingMatrixFilter.atom';
import { newRegulatoryTrainingMatrixPageAtom } from '@/state/NewRegulatoryTrainingMatrixPage.atom';
import {
	NewRegulatoryTrainingMatrixData,
	NewRegulatoryTrainingMatrixIndicatorsData,
} from '@/types/NewRegulatoryTrainingData';
import { downloadFile } from '@/utils/downloadFile';
import { getActualRole } from '@/utils/handleSavePermissions';

export const useRegulatoryTrainingMatrix = () => {
	const [currentPage, setCurrentPage] = useRecoilState(
		newRegulatoryTrainingMatrixPageAtom,
	);
	const [filters, setFilters] = useRecoilState(
		newRegulatoryTrainingMatrixFilterAtom,
	);
	const {
		data: matrixData,
		isLoading: isLoadingMatrix,
		isError: isErrorMatrix,
		refetch: refetchMatrix,
	} = useQuery<NewRegulatoryTrainingMatrixData>({
		queryKey: ['new-regulatory-training-matrix', currentPage, filters],
		queryFn: async () => {
			const filterParams = buildFilterParams();
			const response = await api.get(
				'/new-regulatory-training-link/new-regulatory-training-matrix',
				{
					params: {
						page: currentPage,
						...filterParams,
					},
				},
			);
			return response.data;
		},
	});
	const fetchFiltersOptions = async () => {
		const response = await api.get(
			'/new-regulatory-training-link/get-filters-options',
		);
		const filtersOptions = {
			generalInformationJobTitle: response.data.generalInformationJobTitle.map(
				(it: string) => {
					return {
						label: it,
						value: it,
					};
				},
			),
			workStation: response.data.workStation.map(
				(it: { name: string; id: string }) => {
					return {
						label: it.name,
						value: it.id,
					};
				},
			),
			training: response.data.training.map(
				(it: { name: string; id: string }) => {
					return {
						label: it.name,
						value: it.id,
					};
				},
			),
			company: response.data.company.map((it: { name: string; id: string }) => {
				return {
					label: it.name,
					value: it.id,
				};
			}),
		};
		return filtersOptions;
	};
	const {
		data: filtersOptions,
		isLoading: isLoadingFiltersOptions,
		refetch: refetchFiltersOptions,
	} = useQuery({
		queryKey: ['new-regulatory-training-matrix-filters-options'],
		queryFn: fetchFiltersOptions,
	});

	const {
		data: indicators,
		isLoading: isLoadingIndicators,
		isError: isErrorIndicators,
		refetch: refetchIndicators,
	} = useQuery<NewRegulatoryTrainingMatrixIndicatorsData>({
		queryKey: ['new-regulatory-training-matrix-indicators', filters],
		queryFn: async () => {
			const filterParams = buildFilterParams();
			const response = await api.get(
				'/new-regulatory-training-link/new-regulatory-training-link-indicators',
				{
					params: {
						...filterParams,
					},
				},
			);
			return response.data;
		},
	});

	const buildFilterParams = () => {
		return {
			generalInformationJobTitle: filters.generalInformationJobTitle,
			workStation: filters.workStation,
			training: filters.training,
			company: filters.company,
			onlyUnmappedPositions: filters.onlyUnmappedPositions,
			onlyUnmappedTrainings: filters.onlyUnmappedTrainings,
		};
	};
	const [isDownloading, setIsDownloading] = useState(false);
	const handleDownloadMatrix = async () => {
		try {
			setIsDownloading(true);
			await downloadFile(
				'extraction/new-regulatory-training-matrix',
				'matriz-regulatoria.xlsx',
				undefined,
				buildFilterParams(),
			);
		} catch (_error) {
			toast.error('Erro ao baixar matriz', {
				theme: 'colored',
				toastId: 'error',
			});
		} finally {
			setIsDownloading(false);
		}
	};

	const [isOnEditionMode, setIsOnEditionMode] = useRecoilState(
		newRegulatoryTrainingMatrixEditionModeAtom,
	);
	const [editions, setEditions] = useRecoilState(
		newRegulatoryTrainingMatrixEditionsAtom,
	);
	const [isEditingMatrix, setIsEditingMatrix] = useState(false);

	const handleSaveEdition = async () => {
		setIsEditingMatrix(true);
		const parsedEditions = Object.values(editions).map((value) => ({
			id: value.id,
			checked: value.checked,
			blockChecked: value.blockChecked,
		}));
		try {
			await api.put(
				'/new-regulatory-training-link/save-new-regulatory-training-link',
				{
					matrixEditions: parsedEditions,
				},
			);
			setIsOnEditionMode(false);
			toast.success('Matriz salva com sucesso', {
				theme: 'colored',
				toastId: 'success',
			});
			refetchMatrix();
			refetchIndicators();
			setEditions({});
		} catch (_error) {
			toast.error('Erro ao salvar matriz', {
				theme: 'colored',
				toastId: 'error',
			});
		} finally {
			setIsEditingMatrix(false);
		}
	};

	type MassEditType =
		| 'checkAll'
		| 'uncheckAll'
		| 'blockCheckAll'
		| 'unblockCheckAll';
	const [selectedMassEditType, setSelectedMassEditType] =
		useState<MassEditType | null>(null);
	const [isEditingMass, setIsEditingMass] = useState(false);
	const handleMassEdit = async (trainingId: number) => {
		setIsEditingMass(true);
		try {
			setIsEditingMatrix(true);
			await api.put('/new-regulatory-training-link/mass-edit', {
				filters: {
					...filters,
					training: [trainingId],
				},
				massEditType: selectedMassEditType,
			});
			toast.success('Alterações massivas realizadas com sucesso', {
				theme: 'colored',
				toastId: 'success',
			});
			refetchMatrix();
			refetchIndicators();
			setSelectedMassEditType(null);
			setIsOnEditionMode(false);
		} catch (_error) {
			toast.error('Erro ao realizar alterações massivas', {
				theme: 'colored',
				toastId: 'error',
			});
		} finally {
			setIsEditingMass(false);
			setIsEditingMatrix(false);
		}
	};
	const [isDownloadingModifications, setIsDownloadingModifications] =
		useState(false);

	const handleDownloadModifications = async ({
		startDate,
		endDate,
		allBeforeMayOf26,
	}: {
		startDate?: Date;
		endDate?: Date;
		allBeforeMayOf26?: boolean;
	}) => {
		const fileName = allBeforeMayOf26
			? 'Histórico de Alterações na Matriz de Regulatórios - Versão Antiga'
			: startDate && endDate
				? `Histórico de Alterações na Matriz de Regulatórios - ${startDate.toLocaleDateString('pt-BR', { month: 'long' }).replace(/^\w/, (c) => c.toUpperCase())}-${endDate.getFullYear()}`
				: 'Histórico de Alterações na Matriz de Regulatórios';

		try {
			setIsDownloadingModifications(true);
			await downloadFile(
				'extraction/new-regulatory-training-link-modifications',
				fileName,
				undefined,
				{
					startDate,
					endDate,
					allBeforeMayOf26,
				},
			);
		} catch (_error) {
			toast.error('Erro ao baixar modificações', {
				theme: 'colored',
				toastId: 'error',
			});
		} finally {
			setIsDownloadingModifications(false);
		}
	};

	const userIsAllowedToCheck = (() => {
		const roles = getActualRole();
		return (
			roles?.includes(UserTypes.Admin) ||
			roles?.includes(UserTypes.QsmsTreinamento)
		);
	})();

	const userIsAllowedToBlock = (() => {
		const roles = getActualRole();
		return (
			roles?.includes(UserTypes.Admin) ||
			roles?.includes(UserTypes.RhTreinamento)
		);
	})();

	const userIsAllowedToCreateTraining = (() => {
		const roles = getActualRole();
		return (
			roles?.includes(UserTypes.Admin) ||
			roles?.includes(UserTypes.QsmsTreinamento)
		);
	})();

	return {
		filters: {
			options: filtersOptions,
			actualFilters: filters,
			setActualFilters: setFilters,
			isLoading: isLoadingFiltersOptions,
			refetch: refetchFiltersOptions,
		},
		matrix: {
			data: matrixData,
			isLoading: isLoadingMatrix,
			isError: isErrorMatrix,
			currentPage,
			setCurrentPage,
			refetch: refetchMatrix,
		},
		indicators: {
			data: indicators,
			isLoading: isLoadingIndicators,
			isError: isErrorIndicators,
			refetch: refetchIndicators,
		},
		downloadMatrix: {
			isDownloading,
			setIsDownloading,
			handleDownloadMatrix,
		},
		editions: {
			isOnEditionMode,
			setIsOnEditionMode,
			editions,
			setEditions,
			handleSaveEdition,
			isEditingMatrix,
		},
		massEdit: {
			isEditingMass,
			setIsEditingMass,
			handleMassEdit,
			selectedMassEditType,
			setSelectedMassEditType,
		},
		downloadModifications: {
			isDownloadingModifications,
			setIsDownloadingModifications,
			handleDownloadModifications,
		},
		permissions: {
			userIsAllowedToCheck,
			userIsAllowedToBlock,
			userIsAllowedToCreateTraining,
		},
	};
};
